import { type ReactNode, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { APIError, ApiErrors } from './api-error';
import { useRealmAuthIsAuthenticated, useRealmAuthReset } from './context';
import { useIxmUserauthV1UserSessionCommandServiceRefreshSession } from './generated/hooks';
import { useWhoami } from './userauth/whoami';

interface AuthorizationProviderProps {
  children: ReactNode;
  onMfaRequired: VoidFunction;
  onUnauthenticated: VoidFunction;
}

export const AuthorizationProvider = ({ children, onMfaRequired, onUnauthenticated }: AuthorizationProviderProps) => {
  const refreshTokenTimeoutRef = useRef<number | null>(null);
  const queryClient = useQueryClient();
  const reset = useRealmAuthReset();
  const isAuthenticated = useRealmAuthIsAuthenticated();
  const { data, refetch } = useWhoami(onUnauthenticated);
  const { mutateAsync } = useIxmUserauthV1UserSessionCommandServiceRefreshSession();

  // Set up a timer to refresh the session before it expires
  useEffect(() => {
    if (isAuthenticated) {
      const sessionExpiry = data?.sessionExpiry ? new Date(data.sessionExpiry) : null;

      if (sessionExpiry) {
        const now = new Date();
        const timeUntilExpiry = sessionExpiry.getTime() - now.getTime();
        const refreshTime = timeUntilExpiry - 60 * 1_000; // Refresh 1 minute before expiry
        const sessionId = data?.sessionId;

        if (refreshTime > 0 && sessionId) {
          refreshTokenTimeoutRef.current = window.setTimeout(async () => {
            try {
              await mutateAsync({ sessionId });
              await refetch();
            } catch {
              // If refreshing the session fails, we can assume the user is unauthenticated
              // reset(false);
              // queryClient.clear();
              // onUnauthenticated();
            }
          }, refreshTime);
        }
      }
    }

    return () => {
      if (refreshTokenTimeoutRef.current) {
        clearTimeout(refreshTokenTimeoutRef.current);
      }
    };
  }, [isAuthenticated, mutateAsync, refetch, data?.sessionExpiry, data?.sessionId]);

  useEffect(() => {
    const priorQueryErrorHandler = queryClient.getQueryCache().config.onError;
    const priorMutationErrorHandler = queryClient.getMutationCache().config.onError;

    function handleError(error: Error) {
      if (error instanceof APIError) {
        switch (error.message.toLowerCase()) {
          case ApiErrors.Unauthenticated:
          case ApiErrors.InvalidSession:
          case ApiErrors.NoSession:
            reset(false);
            queryClient.clear();
            onUnauthenticated();
            return;
          case ApiErrors.MfaRequired:
            onMfaRequired();
            return;
        }
      }
    }

    queryClient.getQueryCache().config.onError = handleError;
    queryClient.getMutationCache().config.onError = handleError;

    return () => {
      queryClient.getQueryCache().config.onError = priorQueryErrorHandler;
      queryClient.getMutationCache().config.onError = priorMutationErrorHandler;
    };
  }, [queryClient, reset, onUnauthenticated, onMfaRequired]);

  return children;
};
